import { useMutation } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import Page from "components/base/Page";
import { InlineSpinner } from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import { useEffect, useRef, useState } from "react";
import TasksView from "../projects/TasksView";
import ActionsView from "./components/ActionsView";
import ComplianceView from "./components/ComplianceView";
import LogisticsView from "./components/LogisticsView";
import PackagingViews from "./components/PackagingViews";
import ProductsView from "./components/ProductsView";
import ProductView from "./components/ProductView";
import ProjectTimelineView from "./components/ProjectTimelineView";
import PublicReleaseView from "./components/publicRelease";
import RunningChangeProductsView from "./components/RunningChangeProductsView";
import StartUpProjectView from "./components/StartUpProjectView";
import StatusChainView from "./components/StatusChainView";
import TrialOrderView from "./components/TrialOrderView";
import { ARCHIVE_GTM_PROJECT, SAVE_GTM_PROJECT } from "./graphql";
import GTMImagesView from "./GTMImagesView";
import { GTMProjectContext } from "./GTMProjectContext";
import NotesView from "./NotesView";

const GTMProjectForm = ({
  originalGTMProject,
  allMaterials,
  suppliers,
  allLabTestStandards,
  refetch,
}) => {
  const [saveProject, saveProjectRes] = useMutation(SAVE_GTM_PROJECT, {
    onCompleted() {
      Alert("success", "Project Saved.");
    },
    onError(error) {
      Alert("error", parseError(error));
    },
  });
  const [autoSaved, setAutoSaved] = useState(false);
  const timer = useRef(null);

  const [archive, archiveRes] = useMutation(ARCHIVE_GTM_PROJECT, {
    variables: { id: originalGTMProject.id },
    onCompleted() {
      Alert("success", "Project archived.");
    },
    onError(error) {
      Alert("error", parseError(error));
    },
  });

  useEffect(() => {
    if (autoSaved) {
      timer.current = setTimeout(() => {
        setAutoSaved(false);
      }, 3000);
      return () => clearTimeout(timer.current);
    }
  }, [autoSaved]);

  function autoSave(variables) {
    if (timer.current) clearTimeout(timer.current);

    timer.current = setTimeout(() => {
      saveProject({
        variables,
        onCompleted() {
          setAutoSaved(true);
        },
      });
    }, 1000);
  }

  function editNameHandler() {
    const newName = window.prompt(
      "Enter new project name",
      originalGTMProject.name,
    );
    if (newName && newName !== originalGTMProject.name) {
      saveProject({ variables: { id: originalGTMProject.id, name: newName } });
    }
  }

  function archiveHandler() {
    if (window.confirm("Are you sure you want to archive this project?")) {
      archive();
    }
  }

  console.log("originalGTMProject?", originalGTMProject);

  return (
    <GTMProjectContext.Provider
      value={{
        originalGTMProject,
        saveProject,
        saveProjectRes,
        allMaterials,
        suppliers,
        allLabTestStandards,
        autoSave,
        setAutoSaved,
        refetch,
      }}
    >
      <Page
        backTo="./.."
        title={
          <div className="flex items-center space-x-4">
            <div>GTM: {originalGTMProject.name}</div>
            <Button edit onClick={editNameHandler}></Button>
          </div>
        }
        rightButtons={
          <div className="flex items-center space-x-6">
            <InlineSpinner
              size={18}
              className={`
              ${saveProjectRes.loading ? "opacity-100" : "opacity-0"}`}
            />
            {autoSaved && (
              <div className="text-green-600 font-bold">Auto Saved ✓</div>
            )}

            <a
              href={`${process.env.REACT_APP_SERVER_ADMIN_URL + "projects/gotomarketproject/" + originalGTMProject.id}`}
              target="_blank"
            >
              Admin
            </a>
          </div>
        }
      >
        <div className="grid grid-cols-12 gap-6 p-6">
          <div className="col-span-full">
            <StatusChainView
              status={
                originalGTMProject.archivedAt
                  ? "ARCHIVED"
                  : originalGTMProject.state
              }
            />
          </div>

          <div className="col-span-8 space-y-6">
            {["PUBLIC_RELEASE"].includes(originalGTMProject.state) && (
              <PublicReleaseView />
            )}

            {["TRIAL_ORDER", "READY_FOR_ORDERS"].includes(
              originalGTMProject.state,
            ) && <TrialOrderView />}

            <ProductView />
            <PackagingViews />
            <LogisticsView />
            <ComplianceView />
          </div>

          <div className="col-span-4 space-y-6">
            <NotesView />
            <GTMImagesView />
            <ProjectTimelineView />
            <TasksView projectType="GTM" projectId={originalGTMProject.id} />
            <ActionsView />

            {["TRIAL_ORDER", "READY_FOR_ORDERS"].includes(
              originalGTMProject.state,
            ) && <ProductsView />}

            {originalGTMProject.startUpProject.team === "RED" && (
              <StartUpProjectView
                startUpProjectId={originalGTMProject.startUpProject.id}
              />
            )}

            <RunningChangeProductsView />

            {originalGTMProject.startUpProject.team !== "RED" &&
              !originalGTMProject.archivedAt && (
                <div className="flex justify-end">
                  <Button
                    danger
                    onClick={archiveHandler}
                    loading={archiveRes.loading}
                  >
                    Archive Project
                  </Button>
                </div>
              )}
          </div>
        </div>
      </Page>
    </GTMProjectContext.Provider>
  );
};

export default GTMProjectForm;
